import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, waLink } from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowLeft, Clock, GraduationCap, Check } from "lucide-react";
import Seo from "../lib/Seo";

export default function CourseDetail() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);

  useEffect(() => {
    api.get(`/courses/${id}`).then((r) => setCourse(r.data));
  }, [id]);

  if (!course) return <div className="min-h-[60vh] grid place-items-center text-foreground/60">Loading...</div>;

  return (
    <div data-testid="course-detail-page" className="min-h-screen">
      <Seo
        title={`${course.title} — Vedic Astrology Course | YourAstroHub`}
        description={course.description}
        keywords={`${course.title}, Vedic Astrology Course, Jyotish Course, ${course.level}, Bhupaal Bisht`}
        path={`/courses/${course.id}`}
        image={course.cover_image}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Courses", path: "/courses" },
          { name: course.title, path: `/courses/${course.id}` },
        ]}
        schemas={[{
          "@context": "https://schema.org",
          "@type": "Course",
          name: course.title,
          description: course.long_description || course.description,
          provider: { "@type": "Organization", name: "YourAstroHub", sameAs: "https://vedic-destiny-hub-2.preview.emergentagent.com/" },
          image: course.cover_image,
          offers: {
            "@type": "Offer",
            price: course.fees,
            priceCurrency: "INR",
            availability: "https://schema.org/InStock",
            url: `https://vedic-destiny-hub-2.preview.emergentagent.com/courses/${course.id}`,
          },
          hasCourseInstance: {
            "@type": "CourseInstance",
            courseMode: course.type === "live" ? "Online (Live)" : "Online (Self-paced)",
            instructor: { "@type": "Person", name: "Bhupaal Bisht" },
          },
        }]}
      />
      <section className="pt-6 pb-16">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <Link to="/courses" className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-[#D4AF37] mb-8"><ArrowLeft className="w-4 h-4" />All Courses</Link>

          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-2 mb-4">
                <span className={`text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full ${course.type === "live" ? "bg-[#D4AF37] text-[#0B1F3A]" : "bg-[#0F284B] text-[#F5E8D8]"}`}>{course.type === "live" ? "Live Cohort" : "Recorded"}</span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-foreground/60 flex items-center gap-1"><GraduationCap className="w-3 h-3" />{course.level}</span>
              </div>
              <h1 className="font-serif text-4xl md:text-5xl font-light leading-tight mb-5">{course.title}</h1>
              <p className="text-lg text-foreground/75 leading-relaxed mb-6">{course.long_description || course.description}</p>

              <div className="rounded-3xl overflow-hidden aspect-[16/9] mb-10">
                <img src={course.cover_image} alt={course.title} className="w-full h-full object-cover" />
              </div>

              {course.best_for && (
                <div className="mb-8 p-5 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-1">Best For</div>
                  <div className="text-base text-foreground/85">{course.best_for}</div>
                </div>
              )}

              <h2 className="font-serif text-3xl mb-5">Course Modules</h2>
              <ul className="space-y-3">
                {course.modules.map((m, i) => (
                  <li key={i} className="flex items-start gap-3 p-4 rounded-2xl bg-[#F4EFE6] dark:bg-[#0F284B]">
                    <div className="w-6 h-6 rounded-full bg-[#D4AF37] text-[#0B1F3A] grid place-items-center text-xs font-bold shrink-0">{i+1}</div>
                    <span className="text-sm md:text-base">{m}</span>
                  </li>
                ))}
              </ul>
            </div>

            <aside className="lg:col-span-5">
              <div className="sticky top-28 rounded-3xl border border-[#D4AF37]/40 p-8 bg-background">
                <div className="text-xs uppercase tracking-[0.24em] text-foreground/60">Enrollment</div>
                <div className="font-serif text-5xl text-[#D4AF37] mt-2">₹{course.fees.toLocaleString()}</div>
                <div className="text-sm text-foreground/60 mt-1 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{course.duration}</div>
                {course.class_duration && (
                  <div className="text-xs text-foreground/60 mt-1">{course.class_duration}</div>
                )}

                <ul className="mt-6 space-y-3 text-sm">
                  {["Lifetime access to recordings", "Certificate of completion", "Community access", "Live doubt sessions"].map((x) => (
                    <li key={x} className="flex items-center gap-2"><Check className="w-4 h-4 text-[#D4AF37]" />{x}</li>
                  ))}
                </ul>

                <a href={waLink(`Hello, I would like to enroll in "${course.title}" (₹${course.fees}).`)} target="_blank" rel="noreferrer" className="block mt-6">
                  <Button data-testid="course-enroll-btn" size="lg" className="w-full rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold py-6">
                    Enroll Now
                  </Button>
                </a>
                <div className="text-center text-xs text-foreground/60 mt-3">Confirm enrollment via WhatsApp — payment link will be shared.</div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
